import { ActionList, ActionListItem, Alert, AlertVariant, Badge, Bullseye, Button, Checkbox, ClipboardCopy, DatePicker, Divider, DropdownItem, DropdownList, EmptyState, EmptyStateActions, EmptyStateBody, EmptyStateFooter, FormGroup, FormSelect, FormSelectOption, Icon, Label, MenuToggle, MenuToggleElement, Modal, ModalBody, ModalFooter, ModalHeader, ModalVariant, PageSection, Pagination, PaginationVariant, SearchInput, Select, SelectOption, Split, SplitItem, Switch, TextInput, Title, Toolbar, ToolbarContent, ToolbarFilter, ToolbarGroup, ToolbarItem, ToolbarLabel, ToolbarLabelGroup, ToolbarToggleGroup, Tooltip } from '@patternfly/react-core';
import * as React from 'react';
import { Form, useLocation, useNavigate } from 'react-router-dom';

import { KlassApi } from '@app/api';
import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { useNotification } from '@app/AppLayout/NotificationContext';
import InlineEdit from '@app/components/InLineEdit';
import { FlagLevelIcon, FlagLevelText } from '@app/components/FlagLevelIcon';
import { KlassFlagSummary } from '@app/components/KlassFlagSummary';
import { CertificationIcon, certificationSummary, useInstructorOptions } from '@app/components/InstructorName';
import { allocatableInstructors } from '@app/model/Instructor';
import { getCertification } from '@app/model/Skill';
import { ClassDecisionLabel, ClassLocation, ClassRegionLabel, ClassTypeLabel, InstructorLevels, InstructorNames, getKlassLocationText } from '@app/components/KlassLabels';
import { RemoveButton } from '@app/components/RemoveButton';
import { useEntity } from '@app/hooks/useEntity';
import { DELIVERY_RESPONSIBILITY, KLASS_DECISION, KLASS_MODALITY, KLASS_STATUS, KLASS_TYPE, REGION } from '@app/model/Enums';
import { FLAG_LEVELS_BY_SEVERITY, Flag, FlagLevel } from '@app/model/Flag';
import { Instructor } from '@app/model/Instructor';
import {
  AdHocKlass, Klass, PrivateKlass, expectedInstructorCount, exportKlassData,
  hasMissingFinalInstructors, instructorNames, resolveInstructors
} from '@app/model/Klass';
import { Language } from '@app/model/Language';
import { SavedFilter } from '@app/model/SavedFilter';
import { useClientExecutiveStore, useInstructorStore, useKlassStore, useSavedFilterStore } from '@app/store/entityStore';
import { PARAMETER, useParameterStringList } from '@app/store/parameterStore';
import { CLASS_EDIT_PERIOD_WEEKS, GLOBAL_FILTER_ALLOWED_ROLES } from '@app/utils/config';
import { useRowRestore } from '@app/hooks/useRowRestore';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import {
  dateFormater, ddMMyyyyFormat, ddMMyyyyHHmmTzFormat, ddMMyyyyParse, fromIsoDate,
  getSundayXWeeksAgo, toIsoDate, weekNumber
} from '@app/utils/functions';
import { getUserRoles, hasRealmRole, keycloak } from '@app/utils/keycloak';
import { ROLE } from '@app/utils/roles';
import { PageActionsMenu, PageActionsMenuItem } from '@app/components/PageActionsMenu';
import { ExportFormat, exportRecords } from '@app/utils/export';
import { AngleDownIcon, AngleRightIcon, CalendarAltIcon, CheckCircleIcon, ExclamationTriangleIcon, FilterIcon, GlobeAmericasIcon, PaperPlaneIcon, PencilAltIcon, PlayIcon, PowerOffIcon, SaveIcon, SearchIcon } from '@patternfly/react-icons';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { DataLoadError } from '@app/components/DataLoadError';
import {
  SHARE_PARAM, decodeSharedView, emptyFilters, normalizeFilters, readSavedFilter, rollsWithWeeks,
  shareUrl, weekStart, withWeekAnchor
} from './classFilters';

// --- Date Math Helpers ---
const getDynamicWeekRange = (weekOffset: number) => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const diffToMonday = -dayOfWeek;
  
  const sunday = new Date(today);
  sunday.setDate(today.getDate() + diffToMonday + (weekOffset * 7));
  
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);

  return { start: toIsoDate(sunday), end: toIsoDate(saturday) };
};

// --- Session View State ---

/**
 * What the user has set the overview up to show: the filters, where they are in the
 * results, and which row they marked. Kept for the whole session, so leaving Classes and
 * coming back - by the nav menu, a breadcrumb, Back, or a return from a class's detail
 * page - lands on the view they left rather than a reset one.
 *
 * Module memory rather than sessionStorage, which would also survive a reload. A full
 * refresh and a logout are the only two things that should clear this, and both take the
 * browser away from this module, so both clear it for free.
 */
const view = {
  filters: emptyFilters(),
  appliedDates: null as { start: string, end: string } | null,
  page: 1,
  hideCancelled: false,
  /**
   * Starts on, so the overview opens on the classes this team delivers. The ones Red Hat or a
   * third party delivers are still there to be asked for, but they are not what the page is for.
   */
  hideNonNiitDelivery: true,
  /** The rows marked, kept marked until the user clears them. */
  selectedRowIds: [] as number[]
};

// --- Bulk Operation Helpers ---

/**
 * How many classes a bulk operation has in the air at once.
 *
 * One at a time would be slow - every save is a write and a read back after it - and all at
 * once would put a request per selected class on the service in one go. Five keeps a run over
 * a page of classes to a couple of seconds without the service seeing a burst.
 */
const BULK_BATCH_SIZE = 5;

/**
 * Runs one operation over every class given, a few at a time, and reports back the ones it
 * could not finish - by offering id, which is what the user has on screen.
 *
 * Every class is attempted whatever its neighbours do: a bulk operation that stops at the
 * first refusal leaves the user with no way of telling how far it got.
 */
const runBulk = async (klasses: Klass[], operation: (klass: Klass) => Promise<unknown>): Promise<string[]> => {
  const failures: string[] = [];

  for (let i = 0; i < klasses.length; i += BULK_BATCH_SIZE) {
    const batch = klasses.slice(i, i + BULK_BATCH_SIZE);
    const outcomes = await Promise.allSettled(batch.map(operation));
    outcomes.forEach((outcome, index) => {
      if (outcome.status === 'rejected') {
        failures.push(`${batch[index]?.offeringId} (${outcome.reason?.message ?? 'unknown error'})`);
      }
    });
  }

  return failures;
};

/** Offering ids as a readable list, cut short so one alert cannot fill a modal. */
const offeringIdList = (klasses: Klass[], limit = 8): string => {
  const shown = klasses.slice(0, limit).map(k => k.offeringId).join(', ');
  return klasses.length > limit ? `${shown} and ${klasses.length - limit} more` : shown;
};

/**
 * The clipboard for a page that is not in a secure context - a deployment served over plain
 * http, where navigator.clipboard is simply absent. A hidden textarea and the deprecated
 * execCommand still work there, and a copy that silently does nothing is worse than old code.
 */
const legacyCopyToClipboard = (text: string) => {
  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.opacity = '0';
  document.body.appendChild(field);
  field.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(field);
  if (!copied) throw new Error('The browser would not let the page write to the clipboard.');
};

// --- Clean Field Mapping ---
const CATEGORY_MAP: Record<string, string> = {
  'Course Name/SKU': 'name',
  'Offering Id': 'offeringId',
  'Week': 'week',
  'Request Id': 'requestId',
  'Type': 'type',
  'Status': 'status',
  'Instructor': 'instructor',
  'Language': 'language',
  'Flag': 'flag',
  'Client Executive': 'clientExecutive',
  'Region': 'region'
};

const ClassesOverview: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotification } = useNotification();

  const currentUsername = keycloak.tokenParsed?.preferred_username || 'default_user';

  // Which roles may edit a global saved filter is the service's decision, not this page's: it
  // enforces the same setting in SavedFilterResource.canGlobalEdit. So it is read from the service
  // rather than mirrored here, and the two can no longer disagree — a mismatch used to mean
  // offering a button that answers 403. GLOBAL_FILTER_ALLOWED_ROLES is now only the fallback, for
  // a service that cannot be asked.
  const globalEditionRoles = useParameterStringList(
    PARAMETER.SAVED_FILTERS_GLOBAL_EDITION,
    GLOBAL_FILTER_ALLOWED_ROLES
  );
  const globalEditor = getUserRoles().some((role) => globalEditionRoles.includes(role));

  /**
   * Whether the non-NIIT deliveries are the user's to ask for. Everybody else works the classes
   * this team delivers, so for them the toggle is not offered and the view stays as it opens.
   */
  const isAdmin = hasRealmRole(ROLE.ADMIN);

// --- Global Store Connections ---
  const { items: rows, error, retry } = useEntity(useKlassStore);
  const saveKlass = useKlassStore(state => state.save);
  const { items: instructors } = useEntity(useInstructorStore);
  const { items: clientExecutives } = useEntity(useClientExecutiveStore);
  const { items: savedFiltersList } = useEntity((useSavedFilterStore))
  const saveSavedFilter = useSavedFilterStore(state => state.save);
  const removeSavedFilter = useSavedFilterStore(state => state.delete);
  
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = React.useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = React.useState(false);
  const [isInstructorDropdownOpen, setIsInstructorDropdownOpen] = React.useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = React.useState(false);
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = React.useState(false);
  const [isSavedFiltersOpen, setIsSavedFiltersOpen] = React.useState(false);
  
  const [currentCategory, setCurrentCategory] = React.useState<string>('Status');
  
  const [activeSortIndex, setActiveSortIndex] = React.useState<number | null>(null);
  const [activeSortDirection, setActiveSortDirection] = React.useState<'asc' | 'desc' | null>(null);

  // `rollDates` is what makes a saved date range keep its meaning: on, the range is stored
  // against the week it was drawn in and moves on with the weeks; off, the dates are pinned
  // exactly as they stand. See classFilters.withWeekAnchor.
  const [filterModal, setFilterModal] = React.useState<{
    isOpen: boolean; mode: 'save' | 'rename'; inputValue: string; targetId: number | null; global: boolean;
    rollDates: boolean;
  }>({
    isOpen: false, mode: 'save', inputValue: '', targetId: null, global: false, rollDates: true
  });

  // The link built for the current view, held only while its modal is open.
  const [shareLink, setShareLink] = React.useState<string | null>(null);

  /** The shared view already taken out of the URL, so it is not applied - or refused - twice. */
  const consumedShareToken = React.useRef<string | null>(null);

  const [hideCancelled, setHideCancelled] = React.useState<boolean>(view.hideCancelled);
  const [hideNonNiitDelivery, setHideNonNiitDelivery] = React.useState<boolean>(view.hideNonNiitDelivery);

  // Density and page size. Unlike the filters these are lasting display preferences,
  // so they live in a cookie rather than in memory and survive a new session.
  const { perPage, setPerPage } = useTablePreferences('classes', 20);

  // Page and filters come back from `view` below; this only puts the scroll back on the
  // row the user opened, and only on the way back from its detail page.
  const { rememberRow, rowProps } = useRowRestore('classes');

  /**
   * The rows the user has marked, kept marked through every navigation of the session - not
   * just the trip to a class's detail page. This is what the bulk operations in the kebab act
   * on, so nothing adds to it on the user's behalf.
   *
   * A Set rather than a list: every row on screen asks "am I in it?" on every render.
   */
  const [selectedRowIds, setSelectedRowIds] = React.useState<Set<number>>(() => new Set(view.selectedRowIds));

  /**
   * Where a range selection is measured from: the row of the last click that was not a
   * shift+click. Shift+click reaches from here to the row clicked, as a file list does.
   */
  const selectionAnchor = React.useRef<number | null>(null);

  // --- Release Class Modal State ---
  const [releaseModalOpen, setReleaseModalOpen] = React.useState(false);
  const [classToRelease, setClassToRelease] = React.useState<Klass | null>(null);
  const [releaseFinalInstructors, setReleaseFinalInstructors] = React.useState<number[]>([]);
  const [releaseDecision, setReleaseDecision] = React.useState<string>('');
  const [isReleaseInstDropdownOpen, setIsReleaseInstDropdownOpen] = React.useState(false);
  const [releaseInstSearch, setReleaseInstSearch] = React.useState('');
  const releaseInstSearchRef = React.useRef<HTMLInputElement>(null);
  const [releaseConfirmOpen, setReleaseConfirmOpen] = React.useState(false);
  // Set when a short final line-up has been pointed out and the user has yet to press the
  // same button a second time. Cleared whenever the choice changes, so a corrected
  // selection - or a different short one - is warned about again.
  const [shortLineUpWarned, setShortLineUpWarned] = React.useState(false);

  // --- Bulk Operation Modal State ---
  // Both modals act on whatever rows are marked, so neither holds a copy of the classes:
  // they read `selectedKlasses`, which cannot drift out of step with the table.
  const [bulkCEModalOpen, setBulkCEModalOpen] = React.useState(false);
  const [bulkCEId, setBulkCEId] = React.useState<number | null>(null);
  const [isBulkCESelectOpen, setIsBulkCESelectOpen] = React.useState(false);
  const [bulkCESearch, setBulkCESearch] = React.useState('');

  const [bulkReleaseModalOpen, setBulkReleaseModalOpen] = React.useState(false);
  const [bulkReleaseDecision, setBulkReleaseDecision] = React.useState<string>('');
  const [bulkShortLineUpWarned, setBulkShortLineUpWarned] = React.useState(false);

  React.useEffect(() => {
    if (isReleaseInstDropdownOpen && releaseInstSearchRef.current) {
      setTimeout(() => releaseInstSearchRef.current?.focus(), 50);
    }
  }, [isReleaseInstDropdownOpen]);

  // --- Session View State Initializers ---
  const [filters, setFilters] = React.useState<Record<string, string[]>>(view.filters);

  const [appliedDates, setAppliedDates] = React.useState<{ start: string, end: string } | null>(view.appliedDates);

  const [page, setPage] = React.useState<number>(view.page);

  const [inputStartDate, setInputStartDate] = React.useState(appliedDates?.start || '');
  const [inputEndDate, setInputEndDate] = React.useState(appliedDates?.end || '');
  const [inputValue, setInputValue] = React.useState(''); 
  const [langSearch, setLangSearch] = React.useState('');
  const [instSearch, setInstSearch] = React.useState('');
  const [isCEDropdownOpen, setIsCEDropdownOpen] = React.useState(false);
  const [ceSearch, setCeSearch] = React.useState('');

  // --- Persistence Effects ---
  React.useEffect(() => { view.filters = filters; }, [filters]);
  React.useEffect(() => { view.appliedDates = appliedDates; }, [appliedDates]);
  React.useEffect(() => { view.page = page; }, [page]);
  React.useEffect(() => { view.hideCancelled = hideCancelled; }, [hideCancelled]);
  React.useEffect(() => { view.hideNonNiitDelivery = hideNonNiitDelivery; }, [hideNonNiitDelivery]);
  React.useEffect(() => { view.selectedRowIds = [...selectedRowIds]; }, [selectedRowIds]);

  // --- Date Validator ---
  // The floor is the store's own window: filtering below it would only ever come back empty.
  const dateValidator = (date: Date): string => {
    const minDateStr = getSundayXWeeksAgo(CLASS_EDIT_PERIOD_WEEKS);
    const dateStr = toIsoDate(date);
    if (dateStr < minDateStr) {
      return `Date cannot be prior to ${CLASS_EDIT_PERIOD_WEEKS} weeks ago (${ddMMyyyyFormat(fromIsoDate(minDateStr))})`;
    }
    return '';
  };

  // --- Data Retrival ---
  const availableLanguages = React.useMemo(() => {
    const langMap = new Map<string, Language>();
    rows.forEach(r => { if (r.language && r.language.englishName) langMap.set(r.language.englishName, r.language); });
    return Array.from(langMap.values()).sort((a, b) => a.englishName.localeCompare(b.englishName));
  }, [rows]);

  const availableInstructors = React.useMemo(() => {
    const instMap = new Map<string, Instructor>();
    rows.forEach(r => {
      const classInstructors = [...(r.instructors ?? []), ...(r.allocatedInstructors ?? []), ...(r.finalInstructors ?? [])];
      classInstructors.forEach(inst => { if (inst && inst.name) instMap.set(inst.name, inst); });
    });
    return Array.from(instMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  // Shared by every Allocated Instructor cell, so the whole page builds the option list
  // once instead of once per row.
  // Certification depends on the class's course, so the options are built per course rather
  // than once for the table - see useInstructorOptions.
  const instructorOptionsFor = useInstructorOptions(instructors);

  // Same idea for the Client Executive cells: one option list for the whole page.
  const clientExecutiveOptions = React.useMemo(
    () => clientExecutives.map(ce => ({ value: ce.id, content: ce.name })),
    [clientExecutives]
  );

  const availableCEs = React.useMemo(() => {
    const ceMap = new Map<string, string>();
    rows.forEach(r => { 
      if (r.clientExecutive && r.clientExecutive.name) ceMap.set(r.clientExecutive.name, r.clientExecutive.name); 
    });
    return Array.from(ceMap.values()).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  
  // --- Collapse State & Active Filter Count ---
  const [isFiltersExpanded, setIsFiltersExpanded] = React.useState(true);
  
  const activeFilterCount = React.useMemo(() => {
    const fieldFilters = Object.values(filters).reduce((total, arr) => total + (arr?.length || 0), 0);
    const dateFilter = appliedDates ? 1 : 0;
    return fieldFilters + dateFilter;
  }, [filters, appliedDates]);

  // --- Filter Handlers ---
  const applyFrontendFilters = (newFilters: Record<string, string[]>) => {
    beginLoading();
    setTimeout(() => {
      setFilters(newFilters);
      setPage(1);
      requestAnimationFrame(() => setTimeout(() => endLoading(), 0));
    }, 10);
  };

  const onTextSearch = (value: string, _event: any) => {
    if (!value.trim()) return;
    
    // Use the MAP to find the precise state key (e.g. 'Request Id' -> 'requestId')
    const field = CATEGORY_MAP[currentCategory];
    if (field) {
      const prevFilters = filters[field] || [];
      if (!prevFilters.includes(value.trim())) {
        applyFrontendFilters({ ...filters, [field]: [...prevFilters, value.trim()] });
        setInputValue(''); 
      }
    }
  };

  const onDeleteFilter = (category: string | ToolbarLabelGroup, id: string | ToolbarLabel) => {
    if (category === 'Date') {
      setAppliedDates(null); setInputStartDate(''); setInputEndDate(''); setPage(1); return;
    }
    
    const stateKey = CATEGORY_MAP[String(category)];
    if (stateKey && filters[stateKey]) {
      applyFrontendFilters({ ...filters, [stateKey]: filters[stateKey].filter(fil => fil !== id) });
    }
  };

  const clearFilters = () => {
    applyFrontendFilters(emptyFilters());
    setAppliedDates(null); setInputStartDate(''); setInputEndDate(''); 
    setLangSearch(''); setInstSearch(''); setCeSearch('');
  };

  const applySavedFilter = (id: number) => {
    const saved = savedFiltersList.find(f => f.id === id);
    if (saved) {
      // The stored range is read relative to the week it was saved in, so a filter over "the
      // fortnight after next" still means that fortnight however long ago it was set up.
      const { filters: savedFilters, appliedDates: savedDates } = readSavedFilter(saved);

      applyFrontendFilters(savedFilters);
      setAppliedDates(savedDates);
      setInputStartDate(savedDates?.start || '');
      setInputEndDate(savedDates?.end || '');
      setIsSavedFiltersOpen(false);
    }
  };

  /**
   * A view arriving in the URL, from a link someone was sent.
   *
   * Applied once, then taken back out of the address bar: from there on the filters are the
   * session's own, like any others, and a token left in place would go on claiming a view the
   * user has since filtered away from. The dates in a link are the dates that were shared -
   * unlike a saved filter, a link is meant to show the recipient the same classes, not the same
   * stretch of weeks relative to whenever they open it.
   *
   * Each token is taken once, tracked by the ref: StrictMode runs every effect twice on mount,
   * and applying the view again is harmless but saying "this link is damaged" twice over is not.
   */
  React.useEffect(() => {
    const token = new URLSearchParams(location.search).get(SHARE_PARAM);
    if (!token || consumedShareToken.current === token) return;
    consumedShareToken.current = token;

    const shared = decodeSharedView(token);
    if (shared) {
      applyFrontendFilters(shared.filters);
      setAppliedDates(shared.appliedDates);
      setInputStartDate(shared.appliedDates?.start || '');
      setInputEndDate(shared.appliedDates?.end || '');
      setHideCancelled(shared.hideCancelled);
      // A link from an administrator can carry the non-NIIT classes shown. Anybody without the
      // toggle would then be stuck with them, having no way to put them back.
      setHideNonNiitDelivery(isAdmin ? shared.hideNonNiitDelivery : true);
      setActiveSortIndex(shared.sortIndex);
      setActiveSortDirection(shared.sortDirection);
    } else {
      addNotification('Shared view could not be opened', AlertVariant.warning,
        'The link is damaged, or was cut short on its way here. Ask whoever sent it for a fresh one.');
    }

    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  /** The current view as a link to hand over. Nothing is stored; it is all in the URL. */
  const openShareModal = () => setShareLink(shareUrl({
    filters,
    appliedDates,
    hideCancelled,
    hideNonNiitDelivery,
    sortIndex: activeSortIndex,
    sortDirection: activeSortDirection
  }));

  const applyDynamicFilter = (type: 'this-week' | 'next-week' | 'two-weeks' | 'three-weeks') => {
    const offsetMap = { 'this-week': 0, 'next-week': 1, 'two-weeks': 2, 'three-weeks': 3 };
    const range = getDynamicWeekRange(offsetMap[type]);
    
    applyFrontendFilters(emptyFilters());
    setLangSearch(''); setInstSearch(''); setInputValue(''); setCeSearch('');
    
    setAppliedDates(range);
    setInputStartDate(range.start);
    setInputEndDate(range.end);
    setIsSavedFiltersOpen(false);
  };

  const onStatusSelect = (event?: React.MouseEvent<Element, MouseEvent>, selection?: string | number) => {
    if (!event) return;
    const checked = (event.target as HTMLInputElement).checked;
    const currentStatus = filters.status || [];
    const newStatus = checked ? [...currentStatus, String(selection)] : currentStatus.filter(v => v !== String(selection));
    applyFrontendFilters({ ...filters, status: newStatus });
  };

  const onLanguageSelect = (event?: React.MouseEvent<Element, MouseEvent>, selection?: string | number) => {
    if (!event) return;
    const checked = (event.target as HTMLInputElement).checked;
    const currentLang = filters.language || [];
    const newLang = checked ? [...currentLang, String(selection)] : currentLang.filter(v => v !== String(selection));
    applyFrontendFilters({ ...filters, language: newLang });
  };

  const onInstructorSelect = (event?: React.MouseEvent<Element, MouseEvent>, selection?: string | number) => {
    if (!event) return;
    const checked = (event.target as HTMLInputElement).checked;
    const currentInst = filters.instructor || [];
    const newInst = checked ? [...currentInst, String(selection)] : currentInst.filter(v => v !== String(selection));
    applyFrontendFilters({ ...filters, instructor: newInst });
  };

  const onTypeSelect = (event?: React.MouseEvent<Element, MouseEvent>, selection?: string | number) => {
    applyFrontendFilters({ ...filters, type: [String(selection)] });
    setIsFilterDropdownOpen(false);
  };

  const onRegionSelect = (event?: React.MouseEvent<Element, MouseEvent>, selection?: string | number) => {
    if (!event) return;
    const checked = (event.target as HTMLInputElement).checked;
    const currentRegions = filters.region || [];
    const newRegions = checked ? [...currentRegions, String(selection)] : currentRegions.filter(v => v !== String(selection));
    applyFrontendFilters({ ...filters, region: newRegions });
  };

  const onCESelect = (event?: React.MouseEvent<Element, MouseEvent>, selection?: string | number) => {
    if (!event) return;
    const checked = (event.target as HTMLInputElement).checked;
    const currentCEs = filters.clientExecutive || [];
    const newCEs = checked ? [...currentCEs, String(selection)] : currentCEs.filter(v => v !== String(selection));
    applyFrontendFilters({ ...filters, clientExecutive: newCEs });
  };

  // --- Saved Filter Action Modals ---
  const executeRemoveSavedFilter = async (id: number) => {
    beginLoading();
    try {
      await removeSavedFilter(id);
      addNotification('Filter Removed', AlertVariant.info, 'The saved filter has been deleted.');
    } catch (error: any) {
      addNotification('Error deleting filter', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  const openSavedFilterSaveModal = () => setFilterModal({ isOpen: true, mode: 'save', inputValue: '', targetId: null, global: false, rollDates: true });

  const openSavedFilterRenameModal = (filter: SavedFilter, event: React.MouseEvent) => {
    event.stopPropagation();
    const global = !filter.owner || (filter as any).global;
    setFilterModal({ isOpen: true, mode: 'rename', inputValue: filter.name, targetId: filter.id, global: !!global, rollDates: rollsWithWeeks(filter) });
    setIsSavedFiltersOpen(false);
  };


const handleSavedFilterModalSubmit = async () => {
    const trimmedValue = filterModal.inputValue.trim();
    if (!trimmedValue) return;

    beginLoading();
    try {
      if (filterModal.mode === 'save') {
        const newFilter: Partial<SavedFilter> = {
          owner: filterModal.global ? undefined : currentUsername,
          function: 'class',
          name: trimmedValue,
          // Stamped with the current week when the range is to follow the weeks, so applying
          // this filter later moves the dates on by however many weeks have passed.
          filters: filterModal.rollDates ? withWeekAnchor(filters, appliedDates) : normalizeFilters(filters),
          appliedDates: appliedDates
        };
        if (filterModal.global) (newFilter as any).global = true;
        
        // 1. Pass the payload to the store as a NEW record (isNew = true)
        await saveSavedFilter(newFilter, true);
        
        addNotification('Filter Saved', AlertVariant.success, `Successfully saved as "${trimmedValue}"`);
        
      } else if (filterModal.mode === 'rename' && filterModal.targetId) {
        const filterToUpdate = savedFiltersList.find(f => f.id === filterModal.targetId);
        if (!filterToUpdate) return;
        
        const updatedFilter = { ...filterToUpdate, name: trimmedValue };
        
        // 2. Pass the payload to the store as an EXISTING record (isNew = false)
        await saveSavedFilter(updatedFilter, false);
        
        addNotification('Filter Renamed', AlertVariant.success, `Filter successfully renamed to "${trimmedValue}".`);
      }
    } catch (error: any) {
      addNotification('Error saving filter', AlertVariant.danger, error.message);
    } finally {
      setFilterModal({ isOpen: false, mode: 'save', inputValue: '', targetId: null, global: false, rollDates: true });
      endLoading();
    }
  };

  // --- Table Derivations ---
  const filteredRows = React.useMemo(() => {
    return rows.filter((row) => {

      // Client-side Date filtering
      let matchesAppliedDates = true;
      if (appliedDates && (appliedDates.start || appliedDates.end)) {
        if (!row.startDateTime) {
          matchesAppliedDates = false;
        } else {
          const rowDate = new Date(row.startDateTime);
          const rowDateStr = toIsoDate(rowDate);
          if (appliedDates.start && rowDateStr < appliedDates.start) matchesAppliedDates = false;
          if (appliedDates.end && rowDateStr > appliedDates.end) matchesAppliedDates = false;
        }
      }

      const matchesCancelledToggle = !hideCancelled || row.status !== KLASS_STATUS.CANCELLED;

      // Only the classes named to a third party are out while the toggle is on. A Red Hat
      // delivery stays, as does a class carrying no delivery responsibility at all: neither is
      // known to be somebody else's, and hiding either would take classes off the page silently.
      const matchesDeliveryToggle = !hideNonNiitDelivery || row.deliveryResponsibility !== DELIVERY_RESPONSIBILITY.NON_NIIT;

      const matchesName = filters.name?.length === 0 || filters.name?.some(n => {
        const searchTerm = n.toLowerCase();
        return row.course?.name?.toLowerCase().includes(searchTerm) || row.course?.sku?.toLowerCase().includes(searchTerm);
      });
      const matchesType = filters.type?.length === 0 || filters.type?.includes(row.type as string);
      const matchesStatus = filters.status?.length === 0 || filters.status?.includes(row.status as string);
      const matchesOffering = filters.offeringId?.length === 0 || filters.offeringId?.includes(String(row.offeringId));
      
      // Any of the three lists naming the instructor keeps the class: a co-delivery must
      // still turn up when either of its instructors is filtered on.
      const matchesInstructor = filters.instructor?.length === 0 || filters.instructor?.some(name =>
        [...(row.instructors ?? []), ...(row.allocatedInstructors ?? []), ...(row.finalInstructors ?? [])]
          .some(inst => inst?.name === name)
      );

      const matchesWeek = filters.week?.length === 0 || filters.week?.some(w => {
        if (!row.startDateTime) return false;
        return String(weekNumber(row.startDateTime)) === w;
      });

      const matchesLanguage = filters.language?.length === 0 || filters.language?.some(l => row.language?.englishName === l);
      
      // Both private (REQnnnn) and ad hoc (ADRnnnn) classes carry a request id, and it is the same
      // id as the pipeline request they were approved from - so this is what finds the class a
      // request became. Open enrolment classes have none, and never match.
      const matchesRequestId = filters.requestId?.length === 0 || filters.requestId?.some(rId => {
        const currentReqId = (row as PrivateKlass | AdHocKlass).requestId;
        if (!currentReqId) return false;
        return currentReqId.toLowerCase().includes(rId.toLowerCase());
      });

      const matchesFlag = filters.flag?.length === 0 || filters.flag?.some(f => {
        return row.activeFlags?.some((af: Flag) => af.flagLevel === f); 
      });

      const matchesCE = filters.clientExecutive?.length === 0 || filters.clientExecutive?.includes(row.clientExecutive?.name || '');

      // A class the report named no operating unit for matches no region, rather than matching
      // every one of them: filtering on EMEA must not turn up the classes whose unit is unknown.
      const matchesRegion = filters.region?.length === 0 || filters.region?.includes(row.region as string);

      return matchesName && matchesType && matchesStatus && matchesOffering &&
        matchesInstructor && matchesWeek && matchesLanguage &&
        matchesRequestId && matchesFlag && matchesCancelledToggle && matchesDeliveryToggle &&
        matchesCE && matchesRegion && matchesAppliedDates;
    });
  }, [rows, filters, hideCancelled, hideNonNiitDelivery, appliedDates]);

  // A page carried over from earlier in the session can point past the end of the results,
  // because the classes themselves move on while the user is on another page. Fall back to
  // the last page holding rows, rather than showing "no results match" over a list that
  // plainly has some. Guarded on there being any rows at all, so a filter that genuinely
  // matches nothing keeps its empty state.
  React.useEffect(() => {
    const lastPage = Math.ceil(filteredRows.length / perPage);
    if (lastPage > 0 && page > lastPage) setPage(lastPage);
  }, [filteredRows.length, perPage, page]);

  const sortedRows = React.useMemo(() => {
    if (activeSortIndex === null || activeSortDirection === null) return filteredRows;
    
    return [...filteredRows].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (activeSortIndex) {
        case 0: aValue = a.startDateTime ? weekNumber(a.startDateTime) : 0; bValue = b.startDateTime ? weekNumber(b.startDateTime) : 0; break;
        case 1: aValue = a.course?.sku?.toLowerCase() || ''; bValue = b.course?.sku?.toLowerCase() || ''; break;
        case 2: aValue = a.startDateTime ? new Date(a.startDateTime).getTime() : 0; bValue = b.startDateTime ? new Date(b.startDateTime).getTime() : 0; break;
        case 3: aValue = a.modality || ''; bValue = b.modality || ''; break;
        case 4: aValue = a.type || ''; bValue = b.type || ''; break;
        case 5: aValue = instructorNames(a.instructors).toLowerCase(); bValue = instructorNames(b.instructors).toLowerCase(); break;
        case 6: aValue = instructorNames(a.allocatedInstructors).toLowerCase(); bValue = instructorNames(b.allocatedInstructors).toLowerCase(); break;
        case 7: aValue = getKlassLocationText(a).toLowerCase(); bValue = getKlassLocationText(b).toLowerCase(); break;
        case 8: aValue = a.offeringId || ''; bValue = b.offeringId || ''; break;
        case 9: aValue = a.status || ''; bValue = b.status || ''; break;
        case 10: aValue = a.endDateTime ? new Date(a.endDateTime).getTime() : 0; bValue = b.endDateTime ? new Date(b.endDateTime).getTime() : 0; break;
        case 11: aValue = a.decision || ''; bValue = b.decision || ''; break;
        case 12: aValue = instructorNames(a.finalInstructors).toLowerCase(); bValue = instructorNames(b.finalInstructors).toLowerCase(); break;
        case 13: aValue = a.language?.englishName?.toLowerCase() || ''; bValue = b.language?.englishName?.toLowerCase() || ''; break;
        case 14: aValue = a.clientExecutive?.name?.toLowerCase() || ''; bValue = b.clientExecutive?.name?.toLowerCase() || ''; break;
        case 15: aValue = a.tss?.name?.toLowerCase() || ''; bValue = b.tss?.name?.toLowerCase() || ''; break;
        case 16: aValue = a.region || ''; bValue = b.region || ''; break;
      }

      if (aValue < bValue) return activeSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return activeSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRows, activeSortIndex, activeSortDirection]);

  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return sortedRows.slice(startIdx, startIdx + perPage);
  }, [sortedRows, page, perPage]);

  // --- Row Selection ---

  /**
   * A mark on a class the filters have since hidden is a mark the user cannot see, and a bulk
   * operation acting on it would be a change they never asked for. So filtering drops whatever
   * it hides out of the selection. Paging does not: the selection is meant to be built up over
   * several pages, and every page still counts towards the total the kebab reports.
   */
  React.useEffect(() => {
    setSelectedRowIds(prev => {
      if (prev.size === 0) return prev;
      const stillShown = new Set(filteredRows.map(r => r.id));
      const kept = [...prev].filter(id => stillShown.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [filteredRows]);

  /**
   * The marked classes, in the order the table is showing them, which is the order the bulk
   * operations work through and the order the offering ids are copied in.
   */
  const selectedKlasses = React.useMemo(
    () => sortedRows.filter(r => selectedRowIds.has(r.id)),
    [sortedRows, selectedRowIds]
  );

  const selectionCount = selectedKlasses.length;
  const nothingSelected = selectionCount === 0;
  const selectionLabel = `${selectionCount} selected class${selectionCount === 1 ? '' : 'es'}`;

  const clearSelection = () => {
    setSelectedRowIds(new Set());
    selectionAnchor.current = null;
  };

  /** Adds or removes one row, leaving the rest of the selection as it stands. */
  const toggleOneRow = (rowId: number) => setSelectedRowIds(prev => {
    const next = new Set(prev);
    if (!next.delete(rowId)) next.add(rowId);
    return next;
  });

  /**
   * Whether a click landed on a control of the row's own - an inline editor, an action button,
   * the row's checkbox - rather than on the row itself. Those have their own job to do, and
   * reaching for the pencil must not also change what is marked.
   */
  const isRowControl = (target: EventTarget | null | undefined): boolean =>
    !!(target as HTMLElement | null)?.closest?.('button, input, select, textarea, a, [role="option"]');

  /**
   * Marking rows, with the modifiers a file list uses:
   * - click: this row alone, or nothing at all if it was already the only one marked
   * - ctrl/cmd+click: add or remove this row, leaving the rest marked
   * - shift+click: the run from the anchor to here, replacing what was marked
   * - ctrl/cmd+shift+click: that same run added to - or taken out of - what was marked
   *
   * The anchor is the last row clicked without shift, and a shift+click leaves it where it is,
   * so the run can be redrawn from the same end again and again. A shift+click with no anchor
   * on the page - after paging, or on the first click of the session - has no run to draw and
   * so counts as a plain click.
   *
   * Ranges are measured over the page on screen. A run reaching into pages the user cannot see
   * would mark classes they never looked at.
   */
  const selectRow = (event: React.MouseEvent | React.KeyboardEvent | undefined, rowId: number, rowIndex: number) => {
    const additive = !!(event && (event.ctrlKey || event.metaKey));
    const anchorIndex = selectionAnchor.current === null
      ? -1
      : paginatedRows.findIndex(r => r.id === selectionAnchor.current);
    const ranged = !!event?.shiftKey && anchorIndex >= 0;

    if (ranged) {
      const run = paginatedRows
        .slice(Math.min(anchorIndex, rowIndex), Math.max(anchorIndex, rowIndex) + 1)
        .map(r => r.id);

      // Whether the run joins the selection or leaves it follows the row actually clicked, so
      // a second ctrl+shift+click over the same stretch undoes the first.
      if (additive) {
        setSelectedRowIds(prev => {
          const next = new Set(prev);
          const removing = prev.has(rowId);
          run.forEach(id => removing ? next.delete(id) : next.add(id));
          return next;
        });
      } else {
        setSelectedRowIds(new Set(run));
      }
      return;
    }

    selectionAnchor.current = rowId;

    if (additive) {
      toggleOneRow(rowId);
      return;
    }

    setSelectedRowIds(prev => (prev.size === 1 && prev.has(rowId) ? new Set() : new Set([rowId])));
  };

  // The header checkbox covers the page on screen, the same rows a shift+click can reach,
  // and leaves anything marked on another page alone.
  const pageRowIds = React.useMemo(() => paginatedRows.map(r => r.id), [paginatedRows]);
  const wholePageSelected = pageRowIds.length > 0 && pageRowIds.every(id => selectedRowIds.has(id));
  const partOfPageSelected = pageRowIds.some(id => selectedRowIds.has(id));

  const selectWholePage = (selecting: boolean) => {
    setSelectedRowIds(prev => {
      const next = new Set(prev);
      pageRowIds.forEach(id => selecting ? next.add(id) : next.delete(id));
      return next;
    });
    selectionAnchor.current = null;
  };

  const saveUpdatedClass = async (klass: Klass) => {
    try {
      // The store uses KlassApi.update under the hood, and updates the rows instantly!
      await saveKlass(klass, false);
      addNotification('Class updated successfully', AlertVariant.success);
    } catch (error: any) {
      addNotification('Error updating class', AlertVariant.danger, error.message);
    }
  };

  // --- Export ---
  // Exports the whole active view, not just the page on screen: every class left by the
  // filters, in the order the table is currently sorted.
  const exportClasses = (exportFormat: ExportFormat) => {
    if (sortedRows.length === 0) {
      addNotification('Nothing to export', AlertVariant.warning, 'No class matches the current filters.');
      return;
    }
    try {
      const fileName = exportRecords(sortedRows.map(exportKlassData), 'classes', exportFormat, 'Classes');
      addNotification('Export ready', AlertVariant.success,
        `${sortedRows.length} class${sortedRows.length === 1 ? '' : 'es'} exported to ${fileName}`);
    } catch (error: any) {
      addNotification('Error exporting classes', AlertVariant.danger, error.message);
    }
  };

  // --- Bulk Operations ---

  /**
   * One notification for the whole run, naming the classes that would not take the change.
   *
   * A bulk operation is the one place where "it worked" and "it failed" are not the only two
   * answers, and the classes that were left behind are the only ones the user has to go back
   * to - so they are named, by the offering id they are found by.
   */
  const reportBulkOutcome = (title: string, attempted: number, failures: string[], successBody: React.ReactNode) => {
    if (failures.length === 0) {
      addNotification(title, AlertVariant.success, successBody);
      return;
    }

    const applied = attempted - failures.length;
    addNotification(
      applied === 0 ? `${title} failed` : `${title} only partly done`,
      applied === 0 ? AlertVariant.danger : AlertVariant.warning,
      <>
        {applied > 0 && <div>{applied} of {attempted} classes went through.</div>}
        <div>{failures.length === 1 ? 'This one did not' : 'These did not'}: {failures.join('; ')}</div>
      </>
    );
  };

  const openBulkCEModal = () => {
    setBulkCEId(null);
    setBulkCESearch('');
    setBulkCEModalOpen(true);
  };

  const applyBulkClientExecutive = async () => {
    const clientExecutive = clientExecutives.find(ce => ce.id === bulkCEId);
    if (!clientExecutive) return;

    // Held on to before the first save, because each one lands back in the store and rebuilds
    // `selectedKlasses` underneath the run.
    const targets = selectedKlasses;

    beginLoading();
    try {
      const failures = await runBulk(targets, klass => saveKlass({ ...klass, clientExecutive }, false));
      reportBulkOutcome('Client Executive assigned', targets.length, failures,
        `${clientExecutive.name} is now the client executive on ${targets.length} ${targets.length === 1 ? 'class' : 'classes'}.`);
      setBulkCEModalOpen(false);
    } finally {
      endLoading();
    }
  };

  /**
   * Why a class among those marked cannot be released. Both are refusals rather than warnings:
   * releasing a class with nobody named as final instructor tells the delivery side nothing,
   * and a class that is cancelled or already delivered has no release left to make - which is
   * why the row's own release button is not there for one either.
   */
  const bulkReleaseBlockers = React.useMemo(() => ({
    withoutFinalInstructors: selectedKlasses.filter(k => !k.finalInstructors?.length),
    closed: selectedKlasses.filter(k => k.status === KLASS_STATUS.CANCELLED || k.status === KLASS_STATUS.DELIVERED)
  }), [selectedKlasses]);

  const bulkReleaseBlocked =
    bulkReleaseBlockers.withoutFinalInstructors.length > 0 || bulkReleaseBlockers.closed.length > 0;

  // Named final instructors, but fewer than the class was scheduled or allocated for. Not a
  // refusal - the same one-press warning the single class modal gives - so the ones with none
  // at all are left out of it, being refused above.
  const bulkShortOfInstructors = React.useMemo(
    () => selectedKlasses.filter(k => k.finalInstructors?.length && hasMissingFinalInstructors(k)),
    [selectedKlasses]
  );

  const bulkAlreadyReleased = React.useMemo(() => selectedKlasses.filter(k => k.released), [selectedKlasses]);

  const openBulkReleaseModal = () => {
    setBulkReleaseDecision('');
    setBulkShortLineUpWarned(false);
    setBulkReleaseModalOpen(true);
  };

  /**
   * Writes the decision on every marked class and releases it.
   *
   * The final instructors are left exactly as they stand - that is the whole reason this modal
   * has no field for them, and why a class without any is refused rather than released empty.
   */
  const handleBulkRelease = async () => {
    if (bulkShortOfInstructors.length > 0 && !bulkShortLineUpWarned) {
      setBulkShortLineUpWarned(true);
      return;
    }

    const targets = selectedKlasses;

    beginLoading();
    try {
      const failures = await runBulk(targets, async klass => {
        await saveKlass({ ...klass, decision: bulkReleaseDecision as KLASS_DECISION }, false);
        await KlassApi.release(klass.id);
      });
      reportBulkOutcome('Classes released', targets.length, failures,
        `${targets.length} ${targets.length === 1 ? 'class was' : 'classes were'} saved as ${bulkReleaseDecision.replace(/_/g, ' ')} and released.`);
      setBulkReleaseModalOpen(false);
    } finally {
      endLoading();
    }
  };

  /**
   * The marked offering ids, one per line, so they paste straight down a spreadsheet column.
   *
   * Dragging the mouse down the Offering Id column would be the obvious way to get at these,
   * but a browser's own text selection runs in document order: it takes whole rows in between,
   * and no standard setting confines it to one column. So the ids are handed over here instead.
   */
  const copySelectedOfferingIds = async () => {
    const ids = selectedKlasses.map(k => k.offeringId);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(ids.join('\n'));
      } else {
        legacyCopyToClipboard(ids.join('\n'));
      }
      addNotification('Copied to the clipboard', AlertVariant.success,
        `${ids.length} offering id${ids.length === 1 ? '' : 's'}, one to a line.`);
    } catch (error) {
      addNotification('Nothing was copied', AlertVariant.danger, (error as Error).message);
    }
  };

  // Three groups, ruled off from each other. First what changes which classes the view shows -
  // an administrator's only, and absent for everybody else, line and all. Then what acts on the
  // view as a whole: hand it over as a link, or take it away as a file. Last what acts on
  // whichever rows are marked - inactive while none are.
  const pageMenuItems: PageActionsMenuItem[] = [
    ...(isAdmin ? [{
      key: 'toggle-non-niit-delivery',
      label: hideNonNiitDelivery ? 'Show non-NIIT deliveries' : 'Hide non-NIIT deliveries',
      onClick: () => setHideNonNiitDelivery(!hideNonNiitDelivery)
    }] : []),
    { key: 'share', label: 'Share this view...', hasDividerBefore: isAdmin, onClick: openShareModal },
    { key: 'export-csv', label: 'Export view to CSV...', onClick: () => exportClasses('csv'), isDisabled: sortedRows.length === 0 },
    { key: 'export-xlsx', label: 'Export view to Excel...', onClick: () => exportClasses('xlsx'), isDisabled: sortedRows.length === 0 },
    {
      key: 'bulk-client-executive',
      hasDividerBefore: true,
      isDisabled: nothingSelected,
      label: nothingSelected ? 'Assign Client Executive...' : `Assign Client Executive to ${selectionLabel}...`,
      onClick: openBulkCEModal
    },
    {
      key: 'bulk-release',
      isDisabled: nothingSelected,
      label: nothingSelected ? 'Release classes...' : `Release ${selectionLabel}...`,
      onClick: openBulkReleaseModal
    },
    {
      key: 'bulk-copy-offering-ids',
      isDisabled: nothingSelected,
      label: nothingSelected ? 'Copy Offering Ids' : `Copy ${selectionCount} Offering Id${selectionCount === 1 ? '' : 's'}`,
      onClick: copySelectedOfferingIds
    }
  ];

  const getSortParams = (columnIndex: number) => ({
    sortBy: { index: activeSortIndex !== null ? activeSortIndex : undefined, direction: activeSortDirection !== null ? activeSortDirection : undefined, defaultDirection: 'asc' as const },
    onSort: (_event: React.MouseEvent, index: number, direction: 'asc' | 'desc') => { setActiveSortIndex(index); setActiveSortDirection(direction); },
    columnIndex
  });

  /**
   * Who the release modal may offer as a final instructor: the allocation pickers' rule, plus
   * anybody already named - see allocatableInstructors.
   */
  const releaseInstructorChoices = React.useMemo(
    () => allocatableInstructors(instructors, instructors.filter(i => releaseFinalInstructors.includes(i.id))),
    [instructors, releaseFinalInstructors]
  );

  const openReleaseModal = (klass: Klass) => {
    setClassToRelease(klass);

    // Seeded with the line-up already decided, else the allocation, else who the source
    // system named - the same order the rest of the app resolves instructors in.
    setReleaseFinalInstructors(resolveInstructors(klass).map(i => i.id));

    setReleaseDecision((klass as any).decision || '');
    setReleaseInstSearch('');
    setShortLineUpWarned(false);
    setReleaseModalOpen(true);
  };

  /** Adds or removes one instructor from the line-up being released. */
  const toggleReleaseInstructor = (id: number) => {
    setReleaseFinalInstructors(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    setShortLineUpWarned(false);
  };

  // Fewer final instructors than the class was scheduled or allocated for. Both buttons
  // say so once and go ahead on the second press rather than blocking the release.
  const releaseShortOfInstructors = !!classToRelease
    && hasMissingFinalInstructors(classToRelease, releaseFinalInstructors.length);

  /**
   * Gate in front of both modal buttons: the first press on a short line-up only surfaces
   * the warning, the second one carries on into `action`.
   */
  const withShortLineUpCheck = (action: () => void) => {
    if (releaseShortOfInstructors && !shortLineUpWarned) {
      setShortLineUpWarned(true);
      return;
    }
    action();
  };

  const handleReleaseSubmit = async (performRelease: boolean) => {
    if (!classToRelease) return;

    beginLoading();
    try {
      // 1. Prepare updated class payload
      const updatedKlass = { ...classToRelease };
      updatedKlass.finalInstructors = releaseFinalInstructors
        .map(id => instructors.find(i => i.id === id))
        .filter(Boolean) as Instructor[];
      (updatedKlass as any).decision = releaseDecision === '' ? null : releaseDecision;

      // 2. We use the store to save it, so the UI instantly updates
      await saveKlass(updatedKlass, false);

      // 3. Custom Business Logic Action
      if (performRelease) {
        await KlassApi.release(classToRelease.id!);
      }

      addNotification(performRelease ? 'Class saved and released successfully' : 'Class saved successfully', AlertVariant.success);
      setReleaseModalOpen(false);
    } catch (error: any) {
      addNotification('Error processing class', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  // --- UI Render Builders ---
  const buildSavedFiltersDropdown = () => (
    <ToolbarItem>
      <Select
        isOpen={isSavedFiltersOpen} onOpenChange={(isOpen) => setIsSavedFiltersOpen(isOpen)} selected="Apply Saved Filter"
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle ref={toggleRef} onClick={() => setIsSavedFiltersOpen(!isSavedFiltersOpen)} isExpanded={isSavedFiltersOpen} style={{ minWidth: '180px' }}>
            <SaveIcon /> &nbsp; Saved Filters
          </MenuToggle>
        )}
      >
        <DropdownList>
          <DropdownItem onClick={openSavedFilterSaveModal}>+ Save Current View...</DropdownItem>
          <Divider />
          <DropdownItem onClick={() => applyDynamicFilter('this-week')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--pf-v6-global--primary-color--100)' }}>
              <CalendarAltIcon /> This Week&apos;s Classes
            </div>
          </DropdownItem>
          <DropdownItem onClick={() => applyDynamicFilter('next-week')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--pf-v6-global--primary-color--100)' }}>
              <CalendarAltIcon /> Next Week&apos;s Classes
            </div>
          </DropdownItem>
          <DropdownItem onClick={() => applyDynamicFilter('two-weeks')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--pf-v6-global--primary-color--100)' }}>
              <CalendarAltIcon /> Classes in Two Weeks
            </div>
          </DropdownItem>
          <DropdownItem onClick={() => applyDynamicFilter('three-weeks')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--pf-v6-global--primary-color--100)' }}>
              <CalendarAltIcon /> Classes in Three Weeks
            </div>
          </DropdownItem>
          <DropdownItem onClick={() => applyFrontendFilters({ ...filters, flag: [FlagLevel.CRITICAL] })}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--pf-v6-global--danger-color--100)' }}>
              <FlagLevelIcon level={FlagLevel.CRITICAL} /> Classes at Risk
            </div>
          </DropdownItem>
          <Divider />
          {savedFiltersList.length === 0 ? (
            <DropdownItem isDisabled>No saved filters</DropdownItem>
          ) : (
            savedFiltersList.map(filter => {
              const global = !filter.owner || (filter as any).global;
              const rolling = rollsWithWeeks(filter) && !!filter.appliedDates;
              return (
                <DropdownItem key={filter.id} onClick={() => applySavedFilter(filter.id)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '16px' }}>
                    <span style={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {filter.name} {global && <Tooltip content="Global Filter"><GlobeAmericasIcon /></Tooltip>}
                      {rolling && (
                        <Tooltip content="Its date range moves on with the weeks, so it keeps covering the same weeks relative to today">
                          <CalendarAltIcon />
                        </Tooltip>
                      )}
                    </span>
                    { globalEditor && 
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip content="Rename"><span style={{ cursor: 'pointer', color: 'var(--pf-v6-global--link--Color)', display: 'flex', alignItems: 'center' }} onClick={(e) => openSavedFilterRenameModal(filter, e)}><PencilAltIcon /></span></Tooltip>
                        <RemoveButton confirmationMessage={global ? `Are you sure you want to remove this global filter: "${filter.name}"?` : `Are you sure you want to remove this saved filter: "${filter.name}"?`} onRemoveConfirm={() => executeRemoveSavedFilter(filter.id)} />
                      </div>
                    }
                  </div>
                </DropdownItem>
              );
            })
          )}
        </DropdownList>
      </Select>
    </ToolbarItem>
  );

  const buildCategoryDropdown = () => (
    <ToolbarItem>
      <Select
        onSelect={(e, val) => { setCurrentCategory(val as string); setIsCategoryDropdownOpen(false); setInputValue(''); }}
        selected={currentCategory} isOpen={isCategoryDropdownOpen} onOpenChange={(isOpen) => setIsCategoryDropdownOpen(isOpen)}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle ref={toggleRef} onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)} isExpanded={isCategoryDropdownOpen} icon={<FilterIcon />} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
            {currentCategory}
          </MenuToggle>
        )}
      >
        <SelectOption value="Type">Type</SelectOption>
        <SelectOption value="Course Name/SKU">Course Name/SKU</SelectOption>
        <SelectOption value="Instructor">Instructor</SelectOption>
        <SelectOption value="Offering Id">Offering Id</SelectOption>
        <SelectOption value="Request Id">Request Id</SelectOption>
        <SelectOption value="Date">Date</SelectOption>
        <SelectOption value="Status">Status</SelectOption>
        <SelectOption value="Week">Week</SelectOption>
        <SelectOption value="Language">Language</SelectOption>
        <SelectOption value="Region">Region</SelectOption>
        <SelectOption value="Client Executive">Client Executive</SelectOption>
        <SelectOption value="Flag">Flag</SelectOption>
      </Select>
    </ToolbarItem>
  );

  const buildFilterDropdown = () => {
    // Pipeline is deliberately absent: a pipeline request is not a class and never appears in this
    // list. It has its own page, under Classes > Pipeline.
    const typeItems = [
      <SelectOption key="OPEN_ENROLMENT" value="OPEN_ENROLMENT">Open Enrolment</SelectOption>,
      <SelectOption key="PRIVATE" value="PRIVATE">Private</SelectOption>,
      <SelectOption key="AD_HOC" value="AD_HOC">Ad Hoc</SelectOption>
    ];

    const statusItems = ['CANCELLED', 'CONFIRMED', 'DELIVERED', 'GUARANTEED_TO_RUN', 'MOVED', 'PENDING', 'PIPELINE', 'SCHEDULED', 'TENTATIVE'].map(status => (
      <SelectOption hasCheckbox key={status} value={status} isSelected={filters.status?.includes(status)}>
        {status.replace(/_/g, ' ')}
      </SelectOption>
    ));

    const renderTextFilter = (categoryName: string, stateKey: keyof typeof filters) => (
      <ToolbarFilter
        key={categoryName}
        labels={filters[stateKey] || []}
        deleteLabel={onDeleteFilter}
        categoryName={categoryName}
        showToolbarItem={currentCategory === categoryName}
      >
        <SearchInput
          aria-label={`${categoryName} filter`} 
          placeholder={`Filter by ${categoryName}...`} 
          onChange={(_e, val) => setInputValue(val)} 
          value={inputValue} 
          onClear={() => setInputValue('')} 
          onSearch={(_event, value) => onTextSearch(value, _event)}
        />
      </ToolbarFilter>
    );

    const onFlagSelect = (event?: React.MouseEvent<Element, MouseEvent>, selection?: string | number) => {
      if (!event) return;
      const checked = (event.target as HTMLInputElement).checked;
      const currentFlags = filters.flag || [];
      const newFlags = checked ? [...currentFlags, String(selection)] : currentFlags.filter(v => v !== String(selection));
      applyFrontendFilters({ ...filters, flag: newFlags });
    };

    return (
      <React.Fragment>
        {renderTextFilter('Course Name/SKU', 'name')}
        {renderTextFilter('Offering Id', 'offeringId')}
        {renderTextFilter('Week', 'week')}
        {renderTextFilter('Request Id', 'requestId')}
        
        {/* TYPE Filter */}
        <ToolbarFilter labels={filters.type} deleteLabel={onDeleteFilter} categoryName="Type" showToolbarItem={currentCategory === 'Type'}>
          <Select aria-label="Type" onSelect={onTypeSelect} selected={filters.type?.[0] || ''} isOpen={isFilterDropdownOpen} 
            onOpenChange={(isOpen) => setIsFilterDropdownOpen(isOpen)} popperProps={{ minWidth: '100px' }} toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
              <MenuToggle ref={toggleRef} onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)} isExpanded={isFilterDropdownOpen} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
                {filters.type?.[0] || `Any`}
              </MenuToggle>
            )}>
            {typeItems}
          </Select>
        </ToolbarFilter>

        {/* STATUS Filter */}
        <ToolbarFilter labels={filters.status} deleteLabel={onDeleteFilter} categoryName="Status" showToolbarItem={currentCategory === 'Status'}>
          <Select aria-label="Status" isOpen={isFilterDropdownOpen} onOpenChange={(isOpen) => setIsFilterDropdownOpen(isOpen)} 
            popperProps={{ minWidth: '100px' }} onSelect={onStatusSelect} selected={filters.status} toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
              <MenuToggle ref={toggleRef} onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)} isExpanded={isFilterDropdownOpen} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
                Filter by status {filters.status && filters.status.length > 0 && <Badge isRead>{filters.status.length}</Badge>}
              </MenuToggle>
            )}>
            {statusItems}
          </Select>
        </ToolbarFilter>

        {/* INSTRUCTOR Filter */}
        <ToolbarFilter labels={filters.instructor} deleteLabel={onDeleteFilter} categoryName="Instructor" showToolbarItem={currentCategory === 'Instructor'}>
          <Select aria-label="Instructor" isOpen={isInstructorDropdownOpen} onOpenChange={(isOpen) => setIsInstructorDropdownOpen(isOpen)} 
            popperProps={{ minWidth: '200px' }} onSelect={onInstructorSelect} selected={filters.instructor} toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
              <MenuToggle ref={toggleRef} onClick={() => setIsInstructorDropdownOpen(!isInstructorDropdownOpen)} isExpanded={isInstructorDropdownOpen} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
                Filter by instructor {filters.instructor && filters.instructor.length > 0 && <Badge isRead>{filters.instructor.length}</Badge>}
              </MenuToggle>
            )}
          >
            <div style={{ padding: '8px' }}><SearchInput value={instSearch} onChange={(_e, val) => setInstSearch(val)} onClear={() => setInstSearch('')} placeholder="Search instructors..." aria-label="Search instructors input" /></div>
            <Divider />
            {availableInstructors.filter(inst => inst.name.toLowerCase().includes(instSearch.toLowerCase())).map(inst => (
                <SelectOption hasCheckbox key={inst.id || inst.name} value={inst.name} isSelected={filters.instructor?.includes(inst.name)}>{inst.name}</SelectOption>
            ))}
            {availableInstructors.filter(inst => inst.name.toLowerCase().includes(instSearch.toLowerCase())).length === 0 && (
               <SelectOption isDisabled value="No results">No results found</SelectOption>
            )}
          </Select>
        </ToolbarFilter>

        {/* LANGUAGE Filter */}
        <ToolbarFilter labels={filters.language} deleteLabel={onDeleteFilter} categoryName="Language" showToolbarItem={currentCategory === 'Language'}>
          <Select aria-label="Language" isOpen={isLanguageDropdownOpen} onOpenChange={(isOpen) => setIsLanguageDropdownOpen(isOpen)} 
            popperProps={{ minWidth: '200px' }} onSelect={onLanguageSelect} selected={filters.language} toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
              <MenuToggle ref={toggleRef} onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)} isExpanded={isLanguageDropdownOpen} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
                Filter by language {filters.language && filters.language.length > 0 && <Badge isRead>{filters.language.length}</Badge>}
              </MenuToggle>
            )}
          >
            <div style={{ padding: '8px' }}><SearchInput value={langSearch} onChange={(_e, val) => setLangSearch(val)} onClear={() => setLangSearch('')} placeholder="Search languages..." aria-label="Search languages input" /></div>
            <Divider />
            {availableLanguages.filter(lang => lang.englishName.toLowerCase().includes(langSearch.toLowerCase())).map(lang => (
                <SelectOption hasCheckbox key={lang.code} value={lang.englishName} isSelected={filters.language?.includes(lang.englishName)}>{lang.englishName}</SelectOption>
            ))}
            {availableLanguages.filter(lang => lang.englishName.toLowerCase().includes(langSearch.toLowerCase())).length === 0 && (
               <SelectOption isDisabled value="No results">No results found</SelectOption>
            )}
          </Select>
        </ToolbarFilter>

        {/* REGION Filter */}
        {/* Straight off the enum rather than gathered from the rows, unlike Language and
            Instructor: there are four operating units and they do not change, so all four stay
            offered even when the current view happens to hold classes from only one. */}
        <ToolbarFilter labels={filters.region} deleteLabel={onDeleteFilter} categoryName="Region" showToolbarItem={currentCategory === 'Region'}>
          <Select aria-label="Region" isOpen={isRegionDropdownOpen} onOpenChange={(isOpen) => setIsRegionDropdownOpen(isOpen)}
            popperProps={{ minWidth: '150px' }} onSelect={onRegionSelect} selected={filters.region} toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
              <MenuToggle ref={toggleRef} onClick={() => setIsRegionDropdownOpen(!isRegionDropdownOpen)} isExpanded={isRegionDropdownOpen} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
                Filter by region {filters.region && filters.region.length > 0 && <Badge isRead>{filters.region.length}</Badge>}
              </MenuToggle>
            )}
          >
            {Object.values(REGION).map(region => (
              <SelectOption hasCheckbox key={region} value={region} isSelected={filters.region?.includes(region)}>{region}</SelectOption>
            ))}
          </Select>
        </ToolbarFilter>

        {/* DATE Filter */}
        <ToolbarFilter
          labels={appliedDates && (appliedDates.start || appliedDates.end) ? [`${appliedDates.start ? ddMMyyyyFormat(fromIsoDate(appliedDates.start)) : 'Any'} to ${appliedDates.end ? ddMMyyyyFormat(fromIsoDate(appliedDates.end)) : 'Any'}`] : []}
          deleteLabel={onDeleteFilter}
          categoryName="Date"
          showToolbarItem={currentCategory === 'Date'}
        >
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <DatePicker
              value={inputStartDate ? ddMMyyyyFormat(fromIsoDate(inputStartDate)) : ''}
              onChange={(_e, val, date) => {
                if (date && !isNaN(date.getTime())) {
                  setInputStartDate(toIsoDate(date));
                } else {
                  setInputStartDate('');
                }
              }}
              aria-label="Start date"
              dateFormat={dateFormater}
              dateParse={ddMMyyyyParse}
              validators={[dateValidator]}
              placeholder="DD/MM/YYYY"
            />
            <span>to</span>
            <DatePicker
              value={inputEndDate ? ddMMyyyyFormat(fromIsoDate(inputEndDate)) : ''}
              onChange={(_e, val, date) => {
                if (date && !isNaN(date.getTime())) {
                  setInputEndDate(toIsoDate(date));
                } else {
                  setInputEndDate('');
                }
              }}
              aria-label="End date"
              dateFormat={dateFormater}
              dateParse={ddMMyyyyParse}
              validators={[dateValidator]}
              placeholder="DD/MM/YYYY"
            />
            <Button variant="control" onClick={() => { setAppliedDates({ start: inputStartDate, end: inputEndDate }); setPage(1); }} isDisabled={!inputStartDate && !inputEndDate}>Apply</Button>
          </div>
        </ToolbarFilter>

        {/*** Client Executive Search ***/}
        <ToolbarFilter labels={filters.clientExecutive} deleteLabel={onDeleteFilter} categoryName="Client Executive" showToolbarItem={currentCategory === 'Client Executive'}>
          <Select aria-label="Client Executive" isOpen={isCEDropdownOpen} onOpenChange={(isOpen) => setIsCEDropdownOpen(isOpen)} 
            popperProps={{ minWidth: '200px' }} onSelect={onCESelect} selected={filters.clientExecutive} toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
              <MenuToggle ref={toggleRef} onClick={() => setIsCEDropdownOpen(!isCEDropdownOpen)} isExpanded={isCEDropdownOpen} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
                Filter by CE {filters.clientExecutive && filters.clientExecutive.length > 0 && <Badge isRead>{filters.clientExecutive.length}</Badge>}
              </MenuToggle>
            )}
          >
            <div style={{ padding: '8px' }}><SearchInput value={ceSearch} onChange={(_e, val) => setCeSearch(val)} onClear={() => setCeSearch('')} placeholder="Search CEs..." aria-label="Search Client Executives input" /></div>
            <Divider />
            {availableCEs.filter(ce => ce.toLowerCase().includes(ceSearch.toLowerCase())).map(ce => (
                <SelectOption hasCheckbox key={ce} value={ce} isSelected={filters.clientExecutive?.includes(ce)}>{ce}</SelectOption>
            ))}
            {availableCEs.filter(ce => ce.toLowerCase().includes(ceSearch.toLowerCase())).length === 0 && (
               <SelectOption isDisabled value="No results">No results found</SelectOption>
            )}
          </Select>
        </ToolbarFilter>

        {/*** Flag Search ***/}
        <ToolbarFilter 
          categoryName="Flag" 
          showToolbarItem={currentCategory === 'Flag'}
          labels={filters.flag?.map(flag => ({
            key: flag,
            node: getFlagChipNode(flag)
          }))} 
          deleteLabel={(category, chip) => {
            const rawValue = typeof chip === 'string' ? chip : chip.key;
            onDeleteFilter(category, rawValue);
          }} 
        >
          <Select aria-label="Flag" isOpen={isFilterDropdownOpen} onOpenChange={(isOpen) => setIsFilterDropdownOpen(isOpen)} 
            popperProps={{ minWidth: '150px' }} onSelect={onFlagSelect} selected={filters.flag} toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
              <MenuToggle ref={toggleRef} onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)} isExpanded={isFilterDropdownOpen} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
                Filter by flag {filters.flag && filters.flag.length > 0 && <Badge isRead>{filters.flag.length}</Badge>}
              </MenuToggle>
            )}>
            {FLAG_LEVELS_BY_SEVERITY.map(level => (
              <SelectOption key={level} hasCheckbox value={level} isSelected={filters.flag?.includes(level)}>
                <FlagLevelText level={level} />
              </SelectOption>
            ))}
          </Select>
        </ToolbarFilter>
      </React.Fragment>
    );
  };

  const getFlagChipNode = (flag: string) => {
    const level = FLAG_LEVELS_BY_SEVERITY.find(l => l === flag);
    return level ? <FlagLevelText level={level} /> : flag;
  };

  const buildPagination = (variant: 'bottom' | 'top', isCompact: boolean) => (
    <Pagination isCompact={isCompact} itemCount={filteredRows.length} page={page} perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => { setPerPage(newPerPage); setPage(newPage); }}
      variant={variant as PaginationVariant} isStatic
    />
  );

  const formatStatusText = (status: string): string => {
    if (status === 'GUARANTEED_TO_RUN') return 'GTR';
    return status.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const renderClassStatusLabel = (status: KLASS_STATUS | null | undefined) => {
    if (!status) return <>N/A</>;
    const labelText = formatStatusText(status as string);
    switch (status) {
      case KLASS_STATUS.CONFIRMED:
      case KLASS_STATUS.GUARANTEED_TO_RUN: return <Label icon={<PlayIcon/>} color='green'>{labelText}</Label>;
      case KLASS_STATUS.SCHEDULED: return <Label color="blue" status="info">{labelText}</Label>;
      case KLASS_STATUS.TENTATIVE:
      case KLASS_STATUS.PENDING: return <Label color="yellow">{labelText}</Label>;
      case KLASS_STATUS.PIPELINE: return <Label color="purple">{labelText}</Label>;
      case KLASS_STATUS.ON_HOLD:
      case KLASS_STATUS.MOVED: return <Label color="orange">{labelText}</Label>;
      case KLASS_STATUS.CANCELLED: return <Label color="grey" variant='outline' icon={<PowerOffIcon/>}>{labelText}</Label>;
      case KLASS_STATUS.DELIVERED: return <Label variant="outline" status='success'>{labelText}</Label>;
      default: return <Label color="grey">{labelText}</Label>;
    }
  };

  const renderClassModalityLabel = (type: KLASS_MODALITY | null | undefined) => {
    switch (type) {
      case KLASS_MODALITY.VIRTUAL: return <Label color="orange" variant='outline'>Virtual</Label>;
      case KLASS_MODALITY.IN_PERSON: return <Label color="green" variant='outline'>In Person</Label>;
      case KLASS_MODALITY.RHLS_PREMIUM: return <Label color="purple" variant='outline'>RHLS Premium</Label>;
      default: return <>{type != null ? String(type) : ''}</>;
    }
  };

  const compactHeaderStyle = { paddingLeft: '4px', paddingRight: '4px' };

  // Frozen columns. The four leading ones are pinned to the left, Flags and Actions
  // to the right - the flags are what the page is read for, so they stay on screen
  // however far right the table is scrolled.
  // `offset` is the sum of the `width`s between that column and its edge, so the two
  // must stay in sync: Flags parks its right edge on the width of Actions.
  // Week, SKU and Offering Id use modifier="truncate", which pins the rendered width
  // to `width` exactly instead of letting the content stretch the column and break the
  // offsets. Select holds its own width, being one checkbox wide. Actions holds its
  // width by the .cas-col-actions rule in app.css, which trims the icon buttons to fit
  // inside it. Everything the frozen columns give up here is width the scrolling middle
  // gets back, so they are cut to what their content and headers actually need.
  const STICKY = {
    select: { width: '46px', offset: '0' },
    week: { width: '70px', offset: '46px' },
    sku: { width: '100px', offset: '116px' },
    offeringId: { width: '112px', offset: '216px' },
    flags: { width: '88px', offset: '84px' },
    actions: { width: '84px', offset: '0' }
  };

  // The space the table gets: the window less the masthead, title, filters and
  // pagination. The empty and error states claim the same height so their message
  // lands in the middle of the viewport instead of just under the toolbar.
  const tableViewportHeight = isFiltersExpanded ? 'calc(100vh - var(--cas-table-offset))' : 'calc(100vh - var(--cas-table-offset-sm))';

  return (
    <React.Fragment>
      <PageSection hasBodyWrapper isWidthLimited>
        <Split>
          <SplitItem>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Button 
                variant="plain" 
                aria-label="Toggle filters" 
                onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                style={{ padding: 0, marginTop: '4px' }} 
              >
                {isFiltersExpanded ? (
                  <Icon size="xl"><AngleDownIcon /></Icon>
                ) : (
                  <Icon size="xl"><AngleRightIcon /></Icon>
                )}
              </Button>
              <Title headingLevel="h1" size="4xl">Classes</Title>
              
              {!isFiltersExpanded && activeFilterCount > 0 && (
                <Badge isRead>{activeFilterCount} Active Filter{activeFilterCount !== 1 ? 's' : ''}</Badge>
              )}

              {/* Sits beside the title rather than in the toolbar, which the user can collapse:
                  what the bulk operations would act on has to be on screen either way. */}
              {selectionCount > 0 && (
                <Label color="blue" onClose={clearSelection} closeBtnAriaLabel="Clear the selection">
                  {selectionLabel}
                </Label>
              )}
            </div>
          </SplitItem>
          <SplitItem isFilled></SplitItem>
          <SplitItem>
            <ActionList>
              <ActionListItem style={{ display: 'flex', alignItems: 'center', marginRight: '16px' }}>
                <Switch
                  id="hide-cancelled-switch"
                  label="Hide Cancelled"
                  isChecked={hideCancelled}
                  onChange={(_event, checked) => setHideCancelled(checked)}
                  isReversed
                />
              </ActionListItem>
              <ActionListItem><Tooltip content="New class"></Tooltip></ActionListItem>
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} items={pageMenuItems} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      {isFiltersExpanded && (
        <Toolbar id="toolbar-with-chip-groups" clearAllFilters={clearFilters} collapseListedFiltersBreakpoint="xl">
          <ToolbarContent>
            <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="xl">
              <ToolbarGroup variant="filter-group" style={{ lineHeight: '22px', alignItems: 'center' }}>
                {buildSavedFiltersDropdown()}
                <Divider orientation={{ default: 'vertical' }} />
                {buildCategoryDropdown()}
                {buildFilterDropdown()}
              </ToolbarGroup>
            </ToolbarToggleGroup>
            <ToolbarItem variant="pagination" >{buildPagination('top', true)}</ToolbarItem>
          </ToolbarContent>
        </Toolbar>
      )}

      {paginatedRows.length === 0 ? (
        /* Nothing to draw: stand in for the table and fill the space it would have
           taken, so Bullseye has a definite height to centre the message against.
           Distinguishes "the load failed" from "no records match". */
        <div style={{ height: tableViewportHeight, overflow: 'auto' }}>
          {error ? (
            <DataLoadError error={error} entityLabel="classes" onRetry={retry} />
          ) : (
            <Bullseye>
              <EmptyState headingLevel="h5" icon={SearchIcon} titleText="Clear all filters and try again.">
                <EmptyStateBody>No results match the filter criteria. Clear all filters and try again.</EmptyStateBody>
                <EmptyStateFooter><EmptyStateActions><Button variant="link" onClick={clearFilters}>Clear all filters</Button></EmptyStateActions></EmptyStateFooter>
              </EmptyState>
            </Bullseye>
          )}
        </div>
      ) : (
        <OuterScrollContainer style={{ maxHeight: tableViewportHeight, minHeight: 0 }}>
          <InnerScrollContainer>
            {/* gridBreakPoint="" keeps the real table layout at every width, so the
                frozen columns and the horizontal scroll never fall back to stacked cards. */}
            <Table aria-label="Classes to be delivered" isStickyHeader variant='compact' gridBreakPoint=""
              className="cas-wide-table">
              <Thead>
                <Tr>
                  {/* Marks or clears the page on screen - not the whole filtered set, which
                      would put classes the user has never laid eyes on into a bulk operation. */}
                  <Th aria-label="Select the classes on this page" style={compactHeaderStyle}
                    className="cas-col-tight"
                    select={{
                      onSelect: (_event, selecting) => selectWholePage(selecting),
                      isSelected: wholePageSelected,
                      isIndeterminate: partOfPageSelected && !wholePageSelected
                    }}
                    isStickyColumn stickyMinWidth={STICKY.select.width} stickyLeftOffset={STICKY.select.offset} />
                  <Th sort={getSortParams(0)} style={compactHeaderStyle} modifier="truncate"
                    isStickyColumn stickyMinWidth={STICKY.week.width} stickyLeftOffset={STICKY.week.offset}>Week</Th>
                  <Th sort={getSortParams(1)} style={compactHeaderStyle} modifier="truncate" className="cas-col-tight"
                    isStickyColumn stickyMinWidth={STICKY.sku.width} stickyLeftOffset={STICKY.sku.offset}>SKU</Th>
                  <Th sort={getSortParams(8)} style={compactHeaderStyle} modifier="truncate" className="cas-col-tight"
                    isStickyColumn stickyMinWidth={STICKY.offeringId.width} stickyLeftOffset={STICKY.offeringId.offset} hasRightBorder>Offering Id</Th>
                  <Th sort={getSortParams(2)} style={compactHeaderStyle}>Start Date</Th>
                  <Th sort={getSortParams(10)} style={compactHeaderStyle}>End Date</Th>
                  <Th sort={getSortParams(4)} style={compactHeaderStyle}>Type</Th>
                  <Th sort={getSortParams(3)} style={compactHeaderStyle}>Modality</Th>
                  <Th sort={getSortParams(16)} style={compactHeaderStyle}>Region</Th>
                  <Th style={compactHeaderStyle}>Enrollments</Th>
                  <Th sort={getSortParams(9)} style={compactHeaderStyle}>Status</Th>
                  <Th sort={getSortParams(11)} style={compactHeaderStyle}>Decision</Th>
                  <Th sort={getSortParams(5)} style={compactHeaderStyle}>Instructor</Th>
                  <Th sort={getSortParams(6)} style={compactHeaderStyle}>Allocated Instructor</Th>
                  <Th sort={getSortParams(12)} style={compactHeaderStyle}>Final Instructor</Th>
                  <Th style={compactHeaderStyle}>Instructor Level</Th>
                  <Th sort={getSortParams(13)} style={compactHeaderStyle}>Language</Th>
                  <Th sort={getSortParams(7)} style={compactHeaderStyle}>Location</Th>
                  <Th sort={getSortParams(14)} style={compactHeaderStyle}>Client Executive</Th>
                  <Th style={compactHeaderStyle}>GTR</Th>
                  <Th style={compactHeaderStyle}>Published</Th>
                  <Th style={compactHeaderStyle}>Released</Th>
                  <Th sort={getSortParams(15)} style={compactHeaderStyle}>TSS</Th>
                  <Th style={compactHeaderStyle} className="cas-col-tight"
                    isStickyColumn stickyMinWidth={STICKY.flags.width} stickyRightOffset={STICKY.flags.offset} hasLeftBorder>Flags</Th>
                  <Th style={compactHeaderStyle} className="cas-col-tight cas-col-actions"
                    isStickyColumn stickyMinWidth={STICKY.actions.width} stickyRightOffset={STICKY.actions.offset}>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {/* Clicking a row marks it, with ctrl and shift building a selection out of
                    several - see selectRow. Whatever is marked is what the bulk operations in
                    the kebab act on, so the row's own controls are left out of it: reaching for
                    an inline editor or an action button changes nothing about the selection. */}
                {paginatedRows.map((row, rowIndex) => (
                  <Tr key={row.id} {...rowProps(row.id)}
                    isSelectable isClickable
                    isRowSelected={selectedRowIds.has(row.id)}
                    // Shift+click would otherwise drag the browser's own text selection across
                    // every row in between, on top of marking them.
                    onMouseDown={(event) => { if (event.shiftKey) event.preventDefault(); }}
                    onRowClick={(event) => {
                      if (isRowControl(event?.target)) return;
                      selectRow(event, row.id, rowIndex);
                    }}>
                    <Td className="cas-col-tight"
                      select={{
                        rowIndex,
                        isSelected: selectedRowIds.has(row.id),
                        // A checkbox adds and removes one row on its own, the way ctrl+click
                        // does, rather than narrowing the selection down to it.
                        onSelect: () => {
                          toggleOneRow(row.id);
                          selectionAnchor.current = row.id;
                        }
                      }}
                      isStickyColumn stickyMinWidth={STICKY.select.width} stickyLeftOffset={STICKY.select.offset} />
                    <Td dataLabel="Week" modifier="truncate"
                      isStickyColumn stickyMinWidth={STICKY.week.width} stickyLeftOffset={STICKY.week.offset}>
                      {row.startDateTime ? weekNumber(row.startDateTime) : 'N/A'}
                    </Td>
                    <Td dataLabel="SKU" modifier="truncate" className="cas-col-tight" textCenter
                      isStickyColumn stickyMinWidth={STICKY.sku.width} stickyLeftOffset={STICKY.sku.offset}>
                      <Tooltip content={row.course?.name}><span className="tooltipped-text-bold">{row.course?.sku}</span></Tooltip>
                    </Td>
                    <Td dataLabel="Offering Id" modifier="truncate" className="cas-col-tight" textCenter
                      isStickyColumn stickyMinWidth={STICKY.offeringId.width} stickyLeftOffset={STICKY.offeringId.offset} hasRightBorder>
                      {row.offeringId}
                    </Td>
                    <Td dataLabel="Start Date">{row.startDateTime ? ddMMyyyyHHmmTzFormat(row.startDateTime) : 'N/A'}</Td>
                    <Td dataLabel="End Date">{row.endDateTime ? ddMMyyyyHHmmTzFormat(row.endDateTime) : 'N/A'}</Td>
                    <Td dataLabel="Type" textCenter><ClassTypeLabel type={row.type} /></Td>
                    <Td dataLabel="Modality" textCenter>{renderClassModalityLabel(row.modality)}</Td>
                    <Td dataLabel="Region" textCenter><ClassRegionLabel region={row.region} /></Td>
                    <Td dataLabel="Enrollments">
                      <Tooltip
                        position="auto"
                        content={
                          <div style={{ textAlign: 'left', lineHeight: '1.5' }}>
                            <div>Paying: {row.payingDelegates || 0}</div>
                            <div>RHLS Premium: {row.rhlsPremiumDelegates || 0}</div>
                            <div>RHLS Course: {row.rhlsCourseDelegates || 0}</div>
                            <div>Training Units: {row.trainingUnitsDelegates || 0}</div>
                            <div>Free: {row.freeDelegates || 0}</div>
                            <div>Internal: {row.internalDelegates || 0}</div>
                            <div>Bundled: {row.bundledDelegates || 0}</div>
                            <Divider style={{ margin: '8px 0' }} />
                            <div><strong>Booked: {row.delegatesBooked || 0}</strong></div>
                            <div><strong>Total: {row.totalEnrolled || 0}</strong></div>
                            <div><strong>Max: {row.maxDelegates || 0}</strong></div>
                          </div>
                        }
                      >
                        <span className="tooltipped-text">
                          {row.totalEnrolled || 0} / {row.maxDelegates || 0}
                        </span>
                      </Tooltip>
                    </Td>
                    <Td dataLabel="Status" textCenter>{renderClassStatusLabel(row.status)}</Td>
                    <Td dataLabel="Decision" textCenter><ClassDecisionLabel decision={row.decision} /></Td>
                    <Td dataLabel="Instructor"><InstructorNames instructors={row.instructors} course={row.course} /></Td>
                    <Td dataLabel="Allocated Instructor" modifier="nowrap">
                      <InlineEdit type="MultiFilterableSelect"
                        placeholder="Select one or more instructors"
                        value={(row.allocatedInstructors ?? []).map(i => i.id)}
                        decorator={() => <InstructorNames instructors={row.allocatedInstructors} course={row.course} />}
                        onSave={(ids) => saveUpdatedClass({
                          ...row,
                          allocatedInstructors: ((ids ?? []) as (string | number)[])
                            .map(id => instructors.find(i => i.id === id))
                            .filter(Boolean) as Instructor[]
                        })}
                        id="allocatedInstructors"
                        options={instructorOptionsFor(row.course, row.allocatedInstructors)}
                        isCompact={true}
                      />
                    </Td>
                    <Td dataLabel="Final Instructor" modifier="nowrap">
                      <InlineEdit type="MultiFilterableSelect"
                        placeholder="Select one or more instructors"
                        value={(row.finalInstructors ?? []).map(i => i.id)}
                        decorator={() => <InstructorNames instructors={row.finalInstructors} course={row.course} />}
                        onSave={(ids) => saveUpdatedClass({
                          ...row,
                          finalInstructors: ((ids ?? []) as (string | number)[])
                            .map(id => instructors.find(i => i.id === id))
                            .filter(Boolean) as Instructor[]
                        })}
                        id="finalInstructors"
                        options={instructorOptionsFor(row.course, row.finalInstructors)}
                        isCompact={true}
                      />
                    </Td>
                    <Td dataLabel="Instructor Level">
                      <InstructorLevels instructors={resolveInstructors(row)} course={row.course} />
                    </Td>
                    <Td dataLabel="Language">{row.language?.englishName}</Td>
                    <Td dataLabel="Location" tooltip={getKlassLocationText(row)}><ClassLocation klass={row} /></Td>
                    <Td dataLabel="Client Executive" modifier="nowrap">
                      <InlineEdit type="FilterableSelect"
                        placeholder="Select a client executive"
                        value={row.clientExecutive?.id ?? ''}
                        decorator={() => row.clientExecutive?.name || 'N/A'}
                        onSave={(id) => saveUpdatedClass({
                          ...row,
                          clientExecutive: clientExecutives.find(ce => ce.id === id)!
                        })}
                        id="clientExecutive"
                        options={clientExecutiveOptions}
                        isCompact={true}
                      />
                    </Td>
                    <Td dataLabel="GTR">{row.guarantedToRun ? "Yes" : "No"}</Td>
                    <Td dataLabel="Published">{row.published ? "Yes" : "No"}</Td>
                    <Td dataLabel="Released">{row.released ? "Yes" : "No"}</Td>
                    <Td dataLabel="TSS" modifier="nowrap">{row.tss?.name || 'N/A'}</Td>
                    <Td dataLabel="Flags" className="cas-col-tight" textCenter
                      isStickyColumn stickyMinWidth={STICKY.flags.width} stickyRightOffset={STICKY.flags.offset} hasLeftBorder>
                      <KlassFlagSummary flags={row.activeFlags} salient />
                    </Td>
                    <Td dataLabel="Actions" modifier="nowrap" className="cas-col-tight cas-col-actions" textCenter
                      isStickyColumn stickyMinWidth={STICKY.actions.width} stickyRightOffset={STICKY.actions.offset}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
                        <Tooltip content="Edit">
                          <Button
                            icon={<Icon size="lg"><PencilAltIcon /></Icon>}
                            variant="plain"
                            className="compactButtonStyle"
                            onClick={() => {
                              rememberRow(row.id, page);
                              // Marks the row so it is easy to pick out again on the way back -
                              // but only when the user has marked nothing of their own, which
                              // the bulk operations act on and this must not quietly rewrite.
                              setSelectedRowIds(prev => (prev.size === 0 ? new Set([row.id]) : prev));
                              navigate('/classes/' + row.offeringId);
                            }}
                          />
                        </Tooltip>
                        {row.status !== KLASS_STATUS.CANCELLED && row.status !== KLASS_STATUS.DELIVERED && (
                          <Tooltip content="Release Class">
                            <Button
                              icon={
                                <Icon size="lg" status={row.released ? "success" : undefined}>
                                  {row.released ? <CheckCircleIcon /> : <PaperPlaneIcon />}
                                </Icon>
                              }
                              variant="plain"
                              className="compactButtonStyle"
                              onClick={() => openReleaseModal(row)}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}

              </Tbody>
            </Table>
          </InnerScrollContainer>
        </OuterScrollContainer>
      )}
      {buildPagination('bottom', false)}

      <Modal 
        variant={ModalVariant.small} title={filterModal.mode === 'save' ? "Save Current Filter View" : "Rename Saved Filter"}
        isOpen={filterModal.isOpen} onClose={() => setFilterModal({ ...filterModal, isOpen: false })}
      >
        <ModalHeader title={filterModal.mode === 'save' ? "Save Current Filter View" : "Rename Saved Filter"} />
        <ModalBody>
          <p style={{ marginBottom: '12px' }}>{filterModal.mode === 'save' ? "Enter a name for this filter configuration so you can easily apply it later." : "Enter a new name for this saved filter."}</p>
          <TextInput value={filterModal.inputValue} type="text" onChange={(_e, val) => setFilterModal({ ...filterModal, inputValue: val })} aria-label="Filter Name" placeholder={filterModal.mode === 'save' ? "e.g. My Fall Pipeline Classes" : ""} autoFocus />
          {/* Only worth asking about when there is a date range to be relative to. Left on: a
              filter is nearly always set up for a period ahead of today - "the next three weeks",
              "the fortnight after next" - and pinned dates stop meaning that within a week. */}
          {filterModal.mode === 'save' && appliedDates && (appliedDates.start || appliedDates.end) && (
            <div style={{ marginTop: '16px' }}>
              <Checkbox
                id="roll-dates-checkbox"
                label="Move the date range on with the weeks"
                description={`Saved against the week of ${ddMMyyyyFormat(fromIsoDate(weekStart()))}. Applied three weeks from now, the range shifts three weeks too, so it still covers the same weeks relative to that day. Clear this to keep ${appliedDates.start ? ddMMyyyyFormat(fromIsoDate(appliedDates.start)) : 'any'} to ${appliedDates.end ? ddMMyyyyFormat(fromIsoDate(appliedDates.end)) : 'any'} exactly as it stands.`}
                isChecked={filterModal.rollDates}
                onChange={(_e, checked) => setFilterModal({ ...filterModal, rollDates: checked })}
              />
            </div>
          )}
          {globalEditor && (
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Checkbox id="global-filter-checkbox" label="Save as Global Filter (visible to all users)" isChecked={filterModal.global} onChange={(_e, checked) => setFilterModal({ ...filterModal, global: checked })} isDisabled={filterModal.mode === 'rename'} />
          </div>
        )}
        </ModalBody>
        <ModalFooter>
          <Button key="confirm" variant="primary" onClick={handleSavedFilterModalSubmit} isDisabled={!filterModal.inputValue.trim()}>{"Save Filter"}</Button>
          <Button key="cancel" variant="link" onClick={() => setFilterModal({ ...filterModal, isOpen: false })}>Cancel</Button>
        </ModalFooter>
      </Modal>

      <Modal
        variant={ModalVariant.medium}
        title="Share this view"
        isOpen={shareLink !== null}
        onClose={() => setShareLink(null)}
      >
        <ModalHeader title="Share this view" />
        <ModalBody>
          <p style={{ marginBottom: '16px' }}>
            This link opens the Classes page showing exactly what is on screen now: the same
            filters, the same date range, the same sorting. The view travels in the link itself,
            so nothing is saved and whoever you send it to needs only their own CAS sign-in.
          </p>
          <ClipboardCopy isReadOnly hoverTip="Copy" clickTip="Copied">{shareLink ?? ''}</ClipboardCopy>
          <p style={{ marginTop: '16px', color: 'var(--pf-t--global--text--color--subtle, #6a6e73)' }}>
            The dates go as they stand. A shared link always shows the period it was shared
            with, however long it sits in an inbox - unlike a saved filter, which can move its
            range on with the weeks.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button key="close-share" variant="primary" onClick={() => setShareLink(null)}>Close</Button>
        </ModalFooter>
      </Modal>

      <Modal
        variant={ModalVariant.small}
        title="Release Class"
        isOpen={releaseModalOpen}
        onClose={() => setReleaseModalOpen(false)}
      >
        <ModalHeader title="Release Class" />
        <ModalBody>
          {shortLineUpWarned && releaseShortOfInstructors && (
            <Alert
              variant="warning"
              isInline
              title="Fewer final instructors than allocated"
              style={{ marginBottom: '16px' }}
            >
              {classToRelease && (
                <>
                  This class was set up for {expectedInstructorCount(classToRelease)} instructors but only{' '}
                  {releaseFinalInstructors.length} {releaseFinalInstructors.length === 1 ? 'is' : 'are'} named as final.
                  Press the same button again to save it as it stands.
                </>
              )}
            </Alert>
          )}
          <Form>
            <FormGroup label="Final Instructors" fieldId="release-final-instructors">
              <Select
                id="release-instructor-select"
                isOpen={isReleaseInstDropdownOpen}
                onOpenChange={setIsReleaseInstDropdownOpen}
                toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                  <MenuToggle
                    ref={toggleRef}
                    onClick={() => setIsReleaseInstDropdownOpen(!isReleaseInstDropdownOpen)}
                    isExpanded={isReleaseInstDropdownOpen}
                    isFullWidth
                  >
                    {releaseFinalInstructors.length === 0
                      ? 'Select one or more instructors...'
                      : releaseFinalInstructors
                          .map(id => instructors.find(i => i.id === id)?.name)
                          .filter(Boolean)
                          .join(', ')}
                  </MenuToggle>
                )}
                onSelect={(_e, value) => toggleReleaseInstructor(value as number)}
                selected={releaseFinalInstructors}
              >
                <div style={{ padding: '8px' }}>
                  <SearchInput
                    value={releaseInstSearch}
                    ref={releaseInstSearchRef}
                    onChange={(_e, val) => setReleaseInstSearch(val)}
                    onClear={() => setReleaseInstSearch('')}
                    placeholder="Search instructors..."
                  />
                </div>
                <Divider />
                {releaseInstructorChoices
                  .filter(inst => inst.name.toLowerCase().includes(releaseInstSearch.toLowerCase()))
                  .map(inst => {
                    const check = getCertification(inst, classToRelease?.course);
                    return (
                      <SelectOption hasCheckbox key={inst.id} value={inst.id}
                        isSelected={releaseFinalInstructors.includes(inst.id)}
                        icon={check ? <CertificationIcon status={check.status} /> : undefined}
                        description={check ? certificationSummary(check) : undefined}>
                        {inst.name}
                      </SelectOption>
                    );
                  })}
                {releaseInstructorChoices.filter(inst => inst.name.toLowerCase().includes(releaseInstSearch.toLowerCase())).length === 0 && (
                   <SelectOption isDisabled value="No results">No results found</SelectOption>
                )}
              </Select>
            </FormGroup>

            <FormGroup label="Decision" fieldId="release-decision">
              <FormSelect
                id="release-decision"
                value={releaseDecision}
                onChange={(_e, val) => setReleaseDecision(val)}
              >
                <FormSelectOption value="" label="Select a decision..." />
                {/* Dynamically renders options from your KlassDecision enum */}
                {Object.values(KLASS_DECISION).map(decision => (
                  <FormSelectOption
                    key={decision} 
                    value={decision} 
                    label={decision.replace(/_/g, ' ')} 
                  />
                ))}
              </FormSelect>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            key="save-and-release"
            variant="primary"
            onClick={() => withShortLineUpCheck(() => {
              if (classToRelease?.released) {
                setReleaseConfirmOpen(true);
              } else {
                handleReleaseSubmit(true);
              }
            })}
            isDisabled={releaseFinalInstructors.length === 0 || !releaseDecision}
          >
            Save and Release
          </Button>
          <Button key="save-only" variant="secondary" onClick={() => withShortLineUpCheck(() => handleReleaseSubmit(false))}>
            Save Only
          </Button>
          <Button key="cancel" variant="link" onClick={() => setReleaseModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        variant={ModalVariant.small}
        isOpen={releaseConfirmOpen}
        onClose={() => setReleaseConfirmOpen(false)}
      >
        <ModalHeader>
          <Title 
            headingLevel="h1" 
            size="2xl" 
            style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <Icon status="warning"><ExclamationTriangleIcon /></Icon>
            Class Already Released
          </Title>
        </ModalHeader>
        <ModalBody>
          This class has already been released. Are you sure you want to release it again and potentially trigger new notifications?
        </ModalBody>
        <ModalFooter>
          <Button 
            key="confirm-release" 
            variant="primary" 
            onClick={() => {
              setReleaseConfirmOpen(false);
              handleReleaseSubmit(true);
            }}
          >
            Yes, release again
          </Button>
          <Button
            key="cancel-confirm"
            variant="link"
            onClick={() => setReleaseConfirmOpen(false)}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* One client executive onto every marked class. The plainest of the bulk operations:
          nothing about a class can refuse it, so there is nothing to check first. */}
      <Modal
        variant={ModalVariant.small}
        title="Assign Client Executive"
        isOpen={bulkCEModalOpen}
        onClose={() => setBulkCEModalOpen(false)}
      >
        <ModalHeader title="Assign Client Executive" />
        <ModalBody>
          <p style={{ marginBottom: '16px' }}>
            Whoever you pick here becomes the client executive on all {selectionLabel}, replacing
            whoever is named on them now.
          </p>
          <Form>
            <FormGroup label="Client Executive" fieldId="bulk-client-executive" isRequired>
              <Select
                id="bulk-client-executive"
                isOpen={isBulkCESelectOpen}
                onOpenChange={setIsBulkCESelectOpen}
                selected={bulkCEId}
                onSelect={(_e, value) => {
                  setBulkCEId(value as number);
                  setIsBulkCESelectOpen(false);
                }}
                toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                  <MenuToggle
                    ref={toggleRef}
                    onClick={() => setIsBulkCESelectOpen(!isBulkCESelectOpen)}
                    isExpanded={isBulkCESelectOpen}
                    isFullWidth
                  >
                    {clientExecutives.find(ce => ce.id === bulkCEId)?.name ?? 'Select a client executive...'}
                  </MenuToggle>
                )}
              >
                <div style={{ padding: '8px' }}>
                  <SearchInput
                    value={bulkCESearch}
                    onChange={(_e, val) => setBulkCESearch(val)}
                    onClear={() => setBulkCESearch('')}
                    placeholder="Search client executives..."
                    aria-label="Search client executives input"
                  />
                </div>
                <Divider />
                {clientExecutives
                  .filter(ce => ce.name.toLowerCase().includes(bulkCESearch.toLowerCase()))
                  .map(ce => (
                    <SelectOption key={ce.id} value={ce.id} isSelected={ce.id === bulkCEId}>{ce.name}</SelectOption>
                ))}
                {clientExecutives.filter(ce => ce.name.toLowerCase().includes(bulkCESearch.toLowerCase())).length === 0 && (
                  <SelectOption isDisabled value="No results">No results found</SelectOption>
                )}
              </Select>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button key="apply-bulk-ce" variant="primary" onClick={applyBulkClientExecutive} isDisabled={bulkCEId === null}>
            Assign to {selectionCount} {selectionCount === 1 ? 'class' : 'classes'}
          </Button>
          <Button key="cancel-bulk-ce" variant="link" onClick={() => setBulkCEModalOpen(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>

      {/* Releasing every marked class at once. No field for the final instructors: a line-up is
          a class-by-class decision, so this one takes what each class already names and refuses
          to go anywhere near a class that names nobody. Which is also why there is no Save Only
          here - there would be nothing to save but the decision. */}
      <Modal
        variant={ModalVariant.medium}
        title="Release Classes"
        isOpen={bulkReleaseModalOpen}
        onClose={() => setBulkReleaseModalOpen(false)}
      >
        <ModalHeader title={`Release ${selectionLabel}`} />
        <ModalBody>
          {bulkReleaseBlockers.withoutFinalInstructors.length > 0 && (
            <Alert
              variant="danger"
              isInline
              title="Some of these classes name no final instructor"
              style={{ marginBottom: '16px' }}
            >
              Releasing a class is what tells the delivery side who is teaching it, so a class
              with an empty Final Instructor has nothing to release. Fill it in on the row, or
              take the class out of the selection:{' '}
              {offeringIdList(bulkReleaseBlockers.withoutFinalInstructors)}.
            </Alert>
          )}

          {bulkReleaseBlockers.closed.length > 0 && (
            <Alert
              variant="danger"
              isInline
              title="Some of these classes are cancelled or already delivered"
              style={{ marginBottom: '16px' }}
            >
              Neither has a release left to make - which is why a row in that state has no
              release button of its own. Take them out of the selection:{' '}
              {offeringIdList(bulkReleaseBlockers.closed)}.
            </Alert>
          )}

          {!bulkReleaseBlocked && bulkShortLineUpWarned && bulkShortOfInstructors.length > 0 && (
            <Alert
              variant="warning"
              isInline
              title="Fewer final instructors than allocated"
              style={{ marginBottom: '16px' }}
            >
              {bulkShortOfInstructors.length === 1 ? 'This class names' : 'These classes name'} fewer
              final instructors than {bulkShortOfInstructors.length === 1 ? 'it was' : 'they were'} set
              up for: {offeringIdList(bulkShortOfInstructors)}. Press Save and Release again to go
              ahead as things stand.
            </Alert>
          )}

          {!bulkReleaseBlocked && bulkAlreadyReleased.length > 0 && (
            <Alert
              variant="info"
              isInline
              title={`${bulkAlreadyReleased.length} of these classes ${bulkAlreadyReleased.length === 1 ? 'has' : 'have'} been released already`}
              style={{ marginBottom: '16px' }}
            >
              Releasing again may set new notifications going: {offeringIdList(bulkAlreadyReleased)}.
            </Alert>
          )}

          <Form>
            <FormGroup label="Decision" fieldId="bulk-release-decision" isRequired>
              <FormSelect
                id="bulk-release-decision"
                value={bulkReleaseDecision}
                onChange={(_e, val) => { setBulkReleaseDecision(val); setBulkShortLineUpWarned(false); }}
                isDisabled={bulkReleaseBlocked}
              >
                <FormSelectOption value="" label="Select a decision..." />
                {Object.values(KLASS_DECISION).map(decision => (
                  <FormSelectOption key={decision} value={decision} label={decision.replace(/_/g, ' ')} />
                ))}
              </FormSelect>
              <p style={{ marginTop: '8px', color: 'var(--pf-t--global--text--color--subtle, #6a6e73)' }}>
                Written to every class in the selection. Their final instructors are left as they
                are.
              </p>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            key="bulk-save-and-release"
            variant="primary"
            onClick={handleBulkRelease}
            isDisabled={bulkReleaseBlocked || !bulkReleaseDecision || nothingSelected}
          >
            Save and Release {selectionCount} {selectionCount === 1 ? 'class' : 'classes'}
          </Button>
          <Button key="cancel-bulk-release" variant="link" onClick={() => setBulkReleaseModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export { ClassesOverview };
